import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Loader2, Copy, Download, Printer, RefreshCcw, UserPlus } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// Settings → Patient Sign-Up
//
// Manages the clinic's reusable self-registration QR/link:
//   * get_self_registration_settings — fetch (lazily creates the row)
//   * set_self_registration_enabled  — turn the public link on/off
//   * regenerate_self_registration_token — void a leaked/printed QR
//
// The QR encodes <origin>/r/<token>. Patients who scan it land on the
// public PublicPatientRegistration page (no login).
// =====================================================================

export default function SettingsSelfRegistration() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);

  const publicUrl = settings?.token ? `${window.location.origin}/r/${settings.token}` : "";

  async function load() {
    setLoading(true);
    const { data, error: err } = await supabase.rpc("get_self_registration_settings");
    setLoading(false);
    if (err) { setError(err.message || "Could not load sign-up settings."); return; }
    setError(null);
    setSettings(data);
  }
  useEffect(() => { load(); }, []);

  async function toggle(next) {
    setBusy(true);
    const { data, error: err } = await supabase.rpc("set_self_registration_enabled", { p_enabled: next });
    setBusy(false);
    if (err) { toast.error(err.message || "Could not update."); return; }
    setSettings(data);
    toast.success(next ? "Online sign-up is now ON." : "Online sign-up is now OFF.");
  }

  async function regenerate() {
    if (!window.confirm("Generate a new link? Any QR codes you've already printed will stop working.")) return;
    setBusy(true);
    const { data, error: err } = await supabase.rpc("regenerate_self_registration_token");
    setBusy(false);
    if (err) { toast.error(err.message || "Could not regenerate."); return; }
    setSettings(data);
    toast.success("New link generated — reprint your QR code.");
  }

  function copyLink() {
    if (!publicUrl) return;
    navigator.clipboard?.writeText(publicUrl);
    toast.success("Link copied.");
  }

  function downloadQR() {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "patient-signup-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  if (loading) {
    return <div data-testid="self-reg-loading" className="text-sm text-slate-400">Loading…</div>;
  }
  if (error) {
    return <div data-testid="self-reg-error" className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700">{error}</div>;
  }

  const enabled = !!settings?.enabled;

  return (
    <div className="max-w-2xl space-y-6" data-testid="self-reg-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
          <UserPlus className="w-5 h-5 text-teal-600" strokeWidth={2} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Patient Sign-Up</h1>
          <p className="text-sm text-slate-500 mt-1">
            Print this QR for your front desk or share the link so new patients can register themselves.
          </p>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Online sign-up</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {enabled ? "Patients can register through your link right now." : "Your link is turned off — scans will see a “closed” message."}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={busy}
          onClick={() => toggle(!enabled)}
          data-testid="self-reg-toggle"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${enabled ? "bg-teal-600" : "bg-slate-300"} disabled:opacity-50`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>

      {/* QR + link */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className={`flex flex-col items-center gap-4 ${enabled ? "" : "opacity-50"}`}>
          <div ref={qrRef} className="p-3 bg-white rounded-xl border border-slate-100" data-testid="self-reg-qr">
            <QRCodeCanvas value={publicUrl || "https://dental-os.app"} size={196} includeMargin level="M" />
          </div>
          <code className="text-xs break-all text-slate-600 text-center" data-testid="self-reg-url">{publicUrl}</code>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 justify-center">
          <ActionButton onClick={copyLink} icon={Copy}>Copy link</ActionButton>
          <ActionButton onClick={downloadQR} icon={Download}>Download QR</ActionButton>
          <ActionButton onClick={() => window.print()} icon={Printer}>Print</ActionButton>
          <ActionButton onClick={regenerate} icon={RefreshCcw} danger disabled={busy} testId="self-reg-regenerate">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Regenerate link
          </ActionButton>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        New patients who sign up here appear in <span className="font-medium text-slate-500">Patients</span> with a
        “Self-registered” badge so your team can review and verify them.
      </p>
    </div>
  );
}

function ActionButton({ onClick, icon: Icon, children, danger, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border transition disabled:opacity-50
        ${danger ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
    >
      {Icon ? <Icon className="w-4 h-4" /> : null}
      {children}
    </button>
  );
}
